import { haversineKm } from '../lib/corridor'
import { notifyForbidden } from '../lib/notifyForbidden'

export interface MbmtVehicle {
  vehicle_id: string
  lat: number | null
  lon: number | null
  speed_kph: number | null
  soc_pct: number | null
  vehicletype: string
  route_code: string
  /** Sticky Up/Down from stop-visit tracking when known — scopes paid ETA to one leg. */
  route_direction?: string
  heading_deg?: number | null
  seen_s: number
  on_air?: boolean
  moving?: boolean
  /** TIMS duty today but GPS is not near any drawn route line (HOD legend Off route). */
  off_route?: boolean
  driver_name?: string
  schedule_code?: string
  schedule_shift?: string
}

export interface MbmtActionable {
  kind: 'breakdown' | 'alert'
  id: string | number
  vehicle_id: string
  category?: string
  severity?: string
  note?: string
  route_code?: string | null
  speed_kph?: number | null
  opened_ts?: number | null
  lat?: number | null
  lon?: number | null
  source?: string
  threshold?: { value: number; measured: Record<string, number> } | null
  /** JSON-encoded (site_ours_alert.detail is text, not jsonb) -- absent on netradyne rows. */
  detail?: string | null
  driver_name?: string
  schedule_code?: string
  schedule_shift?: string
}

export interface MbmtBreakdown {
  breakdown_id: number
  vehicle_id: string
  category: string
  severity: string
  note: string | null
  contact: string | null
  by_whom: string | null
  opened_ts: number | null
  cleared_ts: number | null
  eta_min: number | null
  lat: number | null
  lon: number | null
}

export interface MbmtAction {
  action: string
  by: string | null
  phone: string | null
  note: string | null
  ts: number | null
}

export interface MbmtCode {
  code: string
  name: string
}

async function mbmtFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  })
  const body = await r.json().catch(() => ({}))
  if (!r.ok) {
    const msg = (body as { error?: string }).error ?? `Request failed (${r.status})`
    if (r.status === 403) notifyForbidden(msg)
    throw new Error(msg)
  }
  return body as T
}

export interface MbmtRoute {
  route_code: string
  name: string
  line: [number, number][]
}

export interface MbmtStage {
  stage_id: string
  name: string
  lon: number
  lat: number
  polygon?: [number, number][] | null
}

export async function fetchMbmtLive(): Promise<{
  vehicles: MbmtVehicle[]
  on_air: number
  moving: number
  register: number
}> {
  const data = await mbmtFetch<{
    vehicles: MbmtVehicle[]
    on_air?: number
    moving?: number
    register?: number
  }>('/api/mbmt/live')
  return {
    vehicles: data.vehicles ?? [],
    on_air: data.on_air ?? 0,
    moving: data.moving ?? 0,
    register: data.register ?? 0,
  }
}

export async function fetchMbmtRoutes(): Promise<{ routes: MbmtRoute[]; stages: MbmtStage[] }> {
  const data = await mbmtFetch<{ routes: MbmtRoute[]; stages: MbmtStage[] }>('/api/mbmt/routes')
  return { routes: data.routes ?? [], stages: data.stages ?? [] }
}

export async function fetchMbmtActionables(): Promise<{
  items: MbmtActionable[]
  todayAlertCount: number
}> {
  const data = await mbmtFetch<{ items: MbmtActionable[]; today_alert_count?: number }>('/api/mbmt/actionables')
  return { items: data.items ?? [], todayAlertCount: data.today_alert_count ?? 0 }
}

/** Full IST-day ours alerts for one plate -- duty playback's alert map/timeline source.
 *  Not the capped fleet-wide actionables queue. */
export async function fetchMbmtVehicleDayAlerts(
  vehicleId: string, date = '',
): Promise<MbmtActionable[]> {
  const q = date ? `&date=${encodeURIComponent(date)}` : ''
  const data = await mbmtFetch<{ items: MbmtActionable[] }>(
    `/api/mbmt/vehicle-day-alerts?vehicle_id=${encodeURIComponent(vehicleId)}${q}`,
  )
  return data.items ?? []
}

export async function fetchMbmtBreakdowns(openOnly = true): Promise<MbmtBreakdown[]> {
  const q = openOnly ? '' : '?open_only=false'
  const data = await mbmtFetch<{ breakdowns: MbmtBreakdown[] }>(`/api/mbmt/breakdowns${q}`)
  return data.breakdowns ?? []
}

export async function fetchMbmtCodes(): Promise<MbmtCode[]> {
  const data = await mbmtFetch<{ codes: MbmtCode[] }>('/api/mbmt/codes')
  return data.codes ?? []
}

export async function openMbmtBreakdown(body: {
  vehicle_id: string
  category?: string
  severity?: string
  note?: string
  contact?: string
  by?: string
  phone?: string
  eta_min?: number | null
  lat?: number | null
  lon?: number | null
}): Promise<{ breakdown_id: number; reused?: boolean }> {
  return mbmtFetch('/api/mbmt/breakdowns', { method: 'POST', body: JSON.stringify(body) })
}

export async function actOnMbmtBreakdown(
  id: number,
  action: string,
  extra: { by?: string; phone?: string; note?: string } = {},
): Promise<void> {
  await mbmtFetch(`/api/mbmt/breakdowns/${id}/actions`, {
    method: 'POST',
    body: JSON.stringify({ action, ...extra }),
  })
}

export async function fetchMbmtActionLog(id: number): Promise<MbmtAction[]> {
  const data = await mbmtFetch<{ actions: MbmtAction[] }>(`/api/mbmt/breakdowns/${id}/actions`)
  return data.actions ?? []
}

export interface MbmtTripLogRow {
  trip_id: string
  vehicle_id: string
  vehicletype?: string
  route_code: string | null
  direction: string | null
  is_dead: boolean
  is_lost?: boolean
  is_short?: boolean
  kind?: string
  schedule_trip_id?: string
  origin_stage?: string | null
  destination_stage?: string | null
  started_ts: number | null
  ended_ts: number | null
  duration_sec: number | null
  /** Real GPS-measured elapsed time, distinct from duration_sec (the bronze plan) --
   * absent when the trail had fewer than two points inside the leg's window. */
  actual_duration_sec?: number | null
  schedule_id?: string | null
  time_bucket?: string | null
  distance_gps_km: number | null
  /** TIMS's own scheduled distance for this trip (site_duty_trip.distance_km, carried
   * onto the leg verbatim). Shown next to distance_gps_km as a second plain number --
   * two independent statements about the same trip, with no delta or flag between them.
   * Optional: absent on a pre-migration table, where the endpoint's sql_basic fallback
   * cannot select it. */
  scheduled_km?: number | null
  completed: boolean
  driver_name?: string
  schedule_code?: string
  schedule_shift?: string
  soc_start?: number | null
  soc_end?: number | null
  energy_kwh?: number | null
  kwh_per_km?: number | null
  soc_per_km?: number | null
  discharge_kwh?: number | null
  regen_kwh?: number | null
  discharge_kwh_per_km?: number | null
  regen_kwh_per_km?: number | null
  regen_ratio_pct?: number | null
}

export async function fetchMbmtTripLog(
  limit = 500, dateFrom = '', dateTo = '', vehicleId = '',
): Promise<MbmtTripLogRow[]> {
  const q = new URLSearchParams({ limit: String(limit) })
  if (dateFrom) q.set('date_from', dateFrom)
  if (dateTo) q.set('date_to', dateTo)
  if (vehicleId) q.set('vehicle_id', vehicleId)
  const data = await mbmtFetch<{ rows: MbmtTripLogRow[] }>(`/api/mbmt/reports/trips?${q}`)
  return data.rows ?? []
}

export interface MbmtIntelReportRow {
  route_code: string
  direction: string
  time_bucket: string
  vehicletype: string
  n: number
  p50_min: number | null
  p85_min: number | null
  scheduled_min: number | null
  verdict: 'ok' | 'short' | 'tricky' | 'hazardous' | 'unknown'
}

/** No direction/vehicletype axis -- site_ours_alert carries neither (see
 * serving/mbmt.py's alerts_by_route_bucket docstring) -- so this is a separate,
 * undirected count per (route, time_bucket), not a field on MbmtIntelReportRow. */
export interface MbmtIntelAlertCount {
  route_code: string
  time_bucket: string
  n: number
}

export async function fetchMbmtIntelReport(): Promise<{
  rows: MbmtIntelReportRow[]
  alerts: MbmtIntelAlertCount[]
  windowDays: number
}> {
  const data = await mbmtFetch<{
    rows: MbmtIntelReportRow[]
    alerts: MbmtIntelAlertCount[]
    window_days?: number
  }>('/api/mbmt/reports/intel')
  // Backend's window_days (serving/mbmt.py's INTEL_ALERTS_WINDOW_DAYS) is the real
  // window alerts_by_route_bucket() queried -- 90 here is only a display fallback for a
  // response that predates this field, not a claim about what the backend actually did.
  return { rows: data.rows ?? [], alerts: data.alerts ?? [], windowDays: data.window_days ?? 90 }
}

export interface MbmtScheduleIntelReportRow {
  schedule_code: string
  time_bucket: string
  route_code: string | null
  direction: string | null
  n: number
  p50_min: number | null
  p85_min: number | null
  scheduled_min: number | null
  verdict: 'ok' | 'short' | 'tricky' | 'hazardous' | 'unknown'
}

export async function fetchMbmtScheduleIntelReport(): Promise<MbmtScheduleIntelReportRow[]> {
  const data = await mbmtFetch<{ rows: MbmtScheduleIntelReportRow[] }>(
    '/api/mbmt/reports/schedule-intel',
  )
  return data.rows ?? []
}

export async function fetchMbmtLiveReport(): Promise<MbmtVehicle[]> {
  const data = await mbmtFetch<{ rows: MbmtVehicle[] }>('/api/mbmt/reports/live')
  return data.rows ?? []
}

export interface MbmtShedVisit {
  visit_id: number
  vehicle_id: string
  stage_id: string
  name?: string
  entered_ts: number
  exited_ts: number | null
  duration_sec: number | null
}

export interface MbmtShedReport {
  date_from: string
  date_to: string
  visits: (MbmtShedVisit & { day: string })[]
  scheduled: {
    vehicle_id: string
    kind: string
    schedule_code?: string
    driver_name?: string
    scheduled_ts: number
    arrived?: boolean
    departed?: boolean
    day: string
  }[]
  at_depot_now: string[]
}

export async function fetchMbmtShedReport(dateFrom = '', dateTo = ''): Promise<MbmtShedReport> {
  const q = new URLSearchParams()
  if (dateFrom) q.set('date_from', dateFrom)
  if (dateTo) q.set('date_to', dateTo)
  const qs = q.toString()
  return mbmtFetch<MbmtShedReport>(`/api/mbmt/reports/shed${qs ? `?${qs}` : ''}`)
}

export interface MbmtPunctualityRow {
  vehicle_id: string
  route_code: string
  direction: string
  kind: string
  schedule_code: string
  driver_name: string
  scheduled_ts: number
  actual_ts: number
  delta_min: number
  status: 'late' | 'early' | 'on_time'
}

export interface MbmtPunctualitySummary {
  route_code: string
  kind: string
  n: number
  late: number
  early: number
  on_time: number
  avg_delta_min: number
}

export async function fetchMbmtPunctuality(dateFrom = '', dateTo = ''): Promise<{
  date_from: string
  date_to: string
  rows: (MbmtPunctualityRow & { day: string })[]
  summary: MbmtPunctualitySummary[]
}> {
  const q = new URLSearchParams()
  if (dateFrom) q.set('date_from', dateFrom)
  if (dateTo) q.set('date_to', dateTo)
  const qs = q.toString()
  return mbmtFetch(`/api/mbmt/reports/punctuality${qs ? `?${qs}` : ''}`)
}

export interface MbmtAlertsSummaryRow {
  kind: string
  category: string
  route_code: string
  n: number
  critical: number
}

export interface MbmtAlertsByVehicleRow {
  vehicle_id: string
  n: number
  critical: number
}

/** driver_name is resolved against the TIMS duty roster for that alert's own IST day
 * (today's live roster, or a dedicated historical TIMS fetch for any other day) --
 * "" means TIMS genuinely has no matching duty record for that vehicle at that time
 * (no record at all, or -- with more than one duty that day -- none whose own window
 * covers it), never a guess. */
export interface MbmtAlertsByDriverRow {
  driver_name: string
  n: number
  critical: number
}

export async function fetchMbmtAlertsSummary(
  dateFrom = '', dateTo = '', vehicleId = '', driverName = '',
): Promise<{
  rows: MbmtAlertsSummaryRow[]
  by_vehicle: MbmtAlertsByVehicleRow[]
  by_driver: MbmtAlertsByDriverRow[]
  total: number
  date_from: string
  date_to: string
  live_path: string
}> {
  const q = new URLSearchParams()
  if (dateFrom) q.set('date_from', dateFrom)
  if (dateTo) q.set('date_to', dateTo)
  if (vehicleId) q.set('vehicle_id', vehicleId)
  if (driverName) q.set('driver_name', driverName)
  const qs = q.toString()
  return mbmtFetch(`/api/mbmt/reports/alerts${qs ? `?${qs}` : ''}`)
}

/** GET /api/mbmt/alerts -- the flat per-alert feed MbmtAlertsFeed.tsx polls
 *  (.claude/work/SPEC.md addendum). Shape returned by serving/mbmt.py's
 *  _mbmt_alert_items() -- deliberately NOT the same BusAlert type Nagpur's
 *  BusAlertsFeed.tsx uses (no alert_id/spv here; category/driver_name instead). */
export interface MbmtAlertItem {
  kind: string
  category: string
  severity: string
  route_code: string | null
  vehicle_id: string
  ts: number
  message: string | null
  detail: Record<string, unknown> | null
  lat: number | null
  lon: number | null
  driver_name: string
}

export async function fetchMbmtAlerts(
  dateFrom = '', dateTo = '', vehicleId = '', driverName = '', limit = 500,
): Promise<{ alerts: MbmtAlertItem[]; total: number }> {
  const q = new URLSearchParams()
  if (dateFrom) q.set('date_from', dateFrom)
  if (dateTo) q.set('date_to', dateTo)
  if (vehicleId) q.set('vehicle_id', vehicleId)
  if (driverName) q.set('driver_name', driverName)
  if (limit != null) q.set('limit', String(limit))
  return mbmtFetch(`/api/mbmt/alerts?${q.toString()}`)
}

/** Backs MbmtAlertsFeed.tsx's DriverSearchField dropdown -- MBMT's own equivalent
 *  of /api/reports/driver-roster (that one is Ultratech/v_vehicle_driver_spell-only,
 *  wrong source for MBMT -- see serving/mbmt.py::mbmt_driver_roster's own docstring). */
export async function fetchMbmtDriverRoster(): Promise<string[]> {
  const data = await mbmtFetch<{ drivers: string[] }>('/api/mbmt/driver-roster')
  return data.drivers
}

export interface MbmtDeadMileageRow {
  vehicle_id: string
  iso_week: string
  scheduled_km: number
  dead_km: number
  dead_pct: number | null
  short_km: number
  short_pct: number | null
  lost_km: number
  lost_pct: number | null
}

export async function fetchMbmtDeadMileage(dateFrom = '', dateTo = ''): Promise<{
  rows: MbmtDeadMileageRow[]
  date_from: string
  date_to: string
}> {
  const q = new URLSearchParams()
  if (dateFrom) q.set('date_from', dateFrom)
  if (dateTo) q.set('date_to', dateTo)
  const qs = q.toString()
  return mbmtFetch(`/api/mbmt/reports/dead-mileage${qs ? `?${qs}` : ''}`)
}

/** One row per scheduled trip. tims_* is TIMS's own planned time; cc_* is the
 * GPS depot-boundary evidence (same source Punctuality already uses internally) --
 * null on either side when that end's board row fell outside the day window, never
 * guessed. */
export interface MbmtScheduleTripLogRow {
  vehicle_id: string
  schedule_code: string
  tims_start_time: number | null
  cc_start_time: number | null
  tims_end_time: number | null
  cc_end_time: number | null
  day: string
}

export async function fetchMbmtScheduleTripLog(dateFrom = '', dateTo = ''): Promise<{
  rows: MbmtScheduleTripLogRow[]
  date_from: string
  date_to: string
  count: number
}> {
  const q = new URLSearchParams()
  if (dateFrom) q.set('date_from', dateFrom)
  if (dateTo) q.set('date_to', dateTo)
  const qs = q.toString()
  return mbmtFetch(`/api/mbmt/reports/schedule-trip-log${qs ? `?${qs}` : ''}`)
}

export interface MbmtEnergyRow {
  trip_id: string
  vehicle_id: string
  route_code: string | null
  direction: string | null
  started_ts: number | null
  ended_ts: number | null
  distance_gps_km: number | null
  soc_start: number | null
  soc_end: number | null
  energy_kwh: number | null
  kwh_per_km: number | null
  soc_per_km: number | null
  discharge_kwh?: number | null
  regen_kwh?: number | null
  discharge_kwh_per_km?: number | null
  regen_kwh_per_km?: number | null
  regen_ratio_pct?: number | null
  vehicletype?: string
  /** Legs aggregated into this row -- one row is now a whole vehicle-day, not one leg. */
  n_legs?: number
}

export async function fetchMbmtEnergyReport(
  limit = 500, dateFrom = '', dateTo = '',
): Promise<MbmtEnergyRow[]> {
  const q = new URLSearchParams({ limit: String(limit) })
  if (dateFrom) q.set('date_from', dateFrom)
  if (dateTo) q.set('date_to', dateTo)
  const data = await mbmtFetch<{ rows: MbmtEnergyRow[] }>(`/api/mbmt/reports/energy?${q}`)
  return data.rows ?? []
}

export interface MbmtVehicleEfficiency {
  vehicle_id: string
  last_leg: {
    trip_id: string
    route_code: string | null
    kwh_per_km: number | null
    soc_per_km: number | null
    distance_gps_km: number | null
  } | null
  /** The vehicle's currently-open leg, re-upserted roughly every 10 minutes by
   *  tools/reconcile_site_trip_legs.py's CronJob -- null while the bus has no leg in
   *  progress. Prefer this over last_leg when both are present: last_leg can be a
   *  previous, possibly much older, finished leg. */
  live_leg: {
    trip_id: string
    route_code?: string | null
    direction?: string | null
    kwh_per_km: number | null
    soc_per_km: number | null
    distance_gps_km: number | null
    soc_start?: number | null
    soc_end?: number | null
    energy_kwh?: number | null
    started_ts: number | null
  } | null
  live_soc_pct: number | null
  live_drain_pct_per_hr: number | null
  live_drain_basis: string | null
  /** Battery pack size for this plate's vehicletype, already flowing live from telemetry
   *  (serving/mbmt.py's report_vehicle_efficiency() reads it off the same in-memory
   *  snapshot live_soc_pct comes from) -- null when that plate hasn't carried one yet. */
  capacity_kwh: number | null
}

export async function fetchMbmtVehicleEfficiency(vehicleId: string): Promise<MbmtVehicleEfficiency> {
  return mbmtFetch(`/api/mbmt/reports/vehicle-efficiency?vehicle_id=${encodeURIComponent(vehicleId)}`)
}

export interface MbmtReplayPoint {
  ts: number
  lat: number | null
  lon: number | null
  speed_kph?: number | null
  heading_deg?: number | null
  soc_pct?: number | null
}

export interface MbmtReplayEvent {
  kind: 'halt' | 'geotag' | 'action' | string
  ts: number
  end_ts?: number | null
  event?: string
  label?: string
  stage_id?: string
  name?: string
  lat?: number | null
  lon?: number | null
  action?: string
  by?: string
  note?: string
}

export interface MbmtReplayClip {
  breakdown_id: number
  vehicle_id: string
  category?: string
  severity?: string
  started_ts: number
  ended_ts: number | null
  reported_ts: number
  resolved_ts: number | null
  still_open: boolean
  clip_complete: boolean
  playable: boolean
  points: MbmtReplayPoint[]
  events: MbmtReplayEvent[]
  geotag_count: number
  halt_count: number
  point_count: number
}

export async function fetchMbmtReplay(id: number): Promise<MbmtReplayClip> {
  return mbmtFetch(`/api/mbmt/breakdowns/${id}/replay`)
}

/** The five alert kinds GET /api/mbmt/trip-alerts counts per trip and the replay endpoint
 * tags each marker with (site_ours_alert.kind's own values) -- kept as a single list so
 * the table's columns and the replay legend can never drift out of sync with each other.
 * v_mbmt_trip_alerts (db/reports_mbmt_trip_alerts.sql) rolls each up into its own
 * lowercase count column on the row -- TRIP_ALERT_COUNT_FIELD below is the map from one
 * to the other. */
export const MBMT_TRIP_ALERT_KINDS = [
  'HARSH_BRAKE', 'HARSH_ACCEL', 'OVERSPEED', 'UNSCHEDULED_STOP', 'EXCESSIVE_IDLING',
] as const

export type MbmtTripAlertKind = typeof MBMT_TRIP_ALERT_KINDS[number]

/** One row per site_duty_trip row (v_mbmt_trip_alerts) -- a scheduled trip slot, not a
 * GPS-observed leg (site_trip_leg is not read by this view at all). started_ts/ended_ts
 * are the slot's matched window -- actual TIMS departure/arrival when both are real and
 * form a valid (end after start) window, the scheduled starttime/endtime otherwise (both
 * epoch ms via serving/mbmt.py's _ms(), same convention every other MBMT endpoint in that
 * file already returns). Unlike a GPS leg this grain always carries both edges -- neither
 * is ever null -- so "in progress" is carried as its own field instead:
 * `completed === false` means the matched window hasn't ended yet (now() < ended_ts).
 * route is assembled server-side from origin_stage/destination_stage, '?' standing in for
 * either stage when unmapped. */
export interface MbmtTripAlertRow {
  trip_id: string
  route: string | null
  origin_stage: string | null
  destination_stage: string | null
  started_ts: number | null
  ended_ts: number | null
  /** now() >= the matched window's start -- distinguishes a not-yet-started trip
   * (started=false, completed=false) from a genuinely in-progress one (started=true,
   * completed=false); `completed` alone can't tell them apart since "not finished" is
   * true of both. */
  started: boolean
  completed: boolean
  vehicle_id: string
  driver_name: string | null
  schedule_code: string | null
  /** IST calendar day for the duty trip — used to load geotags for trip-detail playback. */
  duty_day?: string | null
  harsh_brake: number
  harsh_accel: number
  overspeed: number
  unscheduled_stop: number
  excessive_idling: number
  total_alerts: number
  alert_counts: Partial<Record<MbmtTripAlertKind, number>>
}

export interface MbmtTripAlertsPage {
  rows: MbmtTripAlertRow[]
  count: number
  total: number
}

const TRIP_ALERT_COUNT_FIELD: Record<MbmtTripAlertKind, keyof MbmtTripAlertRow> = {
  HARSH_BRAKE: 'harsh_brake',
  HARSH_ACCEL: 'harsh_accel',
  OVERSPEED: 'overspeed',
  UNSCHEDULED_STOP: 'unscheduled_stop',
  EXCESSIVE_IDLING: 'excessive_idling',
}

export function mbmtTripAlertCount(row: MbmtTripAlertRow, kind: MbmtTripAlertKind): number {
  return (row[TRIP_ALERT_COUNT_FIELD[kind]] as number | undefined) ?? 0
}

/** The real IST calendar date -- v_mbmt_trip_alerts filters site_duty_trip.duty_day,
 *  documented (serving/mbmt.py) as a plain IST calendar date. A UTC-today default would
 *  default to yesterday and grey today out of a `max` date for the first 5.5 hours of
 *  every IST day. Shared by MbmtTripAlerts.tsx (the picker) and BusOpsShell.tsx (the
 *  Alerts chip's own independent total poll, below). */
export function todayIst(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date())
}

/** IST calendar day `today − days` as YYYY-MM-DD (en-CA). Used to clamp the Alerts
 *  date picker to a trailing month without inventing a second clock. */
export function istDaysAgo(days: number): string {
  const ms = Date.now() - Math.max(0, days) * 86_400_000
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date(ms))
}

/** The IST calendar date an arbitrary epoch-ms timestamp falls on -- for deriving "which
 *  day was this trip on" from the trip's own started_ts/ended_ts, rather than assuming
 *  "today" (a trip opened from a day-spanning rows list is not necessarily today's). */
export function istDateOf(ms: number): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date(ms))
}

/** Trailing-month window for Alerts-per-trip (SPEC Feature 12b): today−30 … today. */
export const TRIP_ALERTS_TRAILING_DAYS = 30

export function tripAlertsMinDate(): string {
  return istDaysAgo(TRIP_ALERTS_TRAILING_DAYS)
}

export interface MbmtRouteEta {
  duration_s: number | null
  duration_in_traffic_s: number | null
  eta_ts: number | null
  next_stop_duration_s?: number | null
  destination_duration_s?: number | null
  source?: string
  error?: string | null
}

/** Live Google Directions ETA (server-side). Playback must never call this. */
export async function fetchMbmtEta(opts: {
  originLat: number
  originLon: number
  destLat: number
  destLon: number
  waypointLat?: number
  waypointLon?: number
}): Promise<MbmtRouteEta> {
  const q = new URLSearchParams({
    origin_lat: String(opts.originLat),
    origin_lon: String(opts.originLon),
    dest_lat: String(opts.destLat),
    dest_lon: String(opts.destLon),
  })
  if (opts.waypointLat != null && opts.waypointLon != null) {
    q.set('waypoint_lat', String(opts.waypointLat))
    q.set('waypoint_lon', String(opts.waypointLon))
  }
  return mbmtFetch<MbmtRouteEta>(`/api/mbmt/eta?${q}`)
}

export async function fetchMbmtTripAlerts(opts?: {
  dateFrom?: string
  dateTo?: string
  vehicleId?: string
  route?: string
  scheduleCode?: string
  hasAlerts?: boolean
  limit?: number
  offset?: number
}): Promise<MbmtTripAlertsPage> {
  const q = new URLSearchParams()
  if (opts?.dateFrom) q.set('date_from', opts.dateFrom)
  if (opts?.dateTo) q.set('date_to', opts.dateTo)
  if (opts?.vehicleId) q.set('vehicle_id', opts.vehicleId)
  if (opts?.route) q.set('route', opts.route)
  if (opts?.scheduleCode) q.set('schedule_code', opts.scheduleCode)
  if (opts?.hasAlerts) q.set('has_alerts', 'true')
  q.set('limit', String(opts?.limit ?? 50))
  q.set('offset', String(opts?.offset ?? 0))
  const qs = q.toString()
  const data = await mbmtFetch<{ rows?: MbmtTripAlertRow[]; count?: number; total?: number }>(
    `/api/mbmt/trip-alerts${qs ? `?${qs}` : ''}`,
  )
  return { rows: data.rows ?? [], count: data.count ?? 0, total: data.total ?? 0 }
}

/** site_ours_alert row, as returned verbatim by the replay endpoint -- ts is already
 * epoch ms (site_ours_alert.ts is bigint epoch-ms, not timestamptz). */
export interface MbmtTripAlertMarker {
  alert_id?: string | number
  kind: string
  lat: number | null
  lon: number | null
  ts: number
  severity?: string | null
  message?: string | null
}

/** GPS path + alert markers for one scheduled trip slot's matched window -- same replay
 * shape convention as MbmtReplayClip above (points is the same MbmtReplayPoint[]), just
 * scoped to a trip-alerts row rather than a breakdown incident. */
export interface MbmtTripAlertReplay {
  trip_id: string
  vehicle_id: string
  driver_name?: string | null
  schedule_code?: string | null
  route?: string | null
  origin_stage?: string | null
  destination_stage?: string | null
  duty_day?: string | null
  started_ts: number | null
  ended_ts: number | null
  playable: boolean
  points: MbmtReplayPoint[]
  point_count?: number
  alerts: MbmtTripAlertMarker[]
  alert_count?: number
}

export async function fetchMbmtTripAlertReplay(tripId: string): Promise<MbmtTripAlertReplay> {
  const data = await mbmtFetch<Partial<MbmtTripAlertReplay>>(
    `/api/mbmt/trip-alerts/${encodeURIComponent(tripId)}/replay`,
  )
  return {
    trip_id: data.trip_id ?? tripId,
    vehicle_id: data.vehicle_id ?? '',
    driver_name: data.driver_name,
    schedule_code: data.schedule_code,
    route: data.route,
    origin_stage: data.origin_stage,
    destination_stage: data.destination_stage,
    duty_day: data.duty_day ?? null,
    started_ts: data.started_ts ?? null,
    ended_ts: data.ended_ts ?? null,
    playable: data.playable ?? false,
    points: data.points ?? [],
    point_count: data.point_count,
    alerts: data.alerts ?? [],
    alert_count: data.alert_count,
  }
}

export interface MbmtTrackPoint {
  ts: number
  lat: number
  lon: number
  speed_kph: number | null
  heading_deg: number | null
  soc_pct: number | null
}

/** Full GPS trail for one vehicle over a real time window, straight from Kafka -- not tied
 * to a breakdown incident. This is what makes "play back any trip" possible at all: the
 * server-side in-memory trail only ever kept 12 minutes for breakdown lead-in. */
export async function fetchMbmtTrack(
  vehicleId: string, minutes = 90,
): Promise<{ vehicle_id: string; minutes: number; count: number; track: MbmtTrackPoint[] }> {
  return mbmtFetch(
    `/api/mbmt/track?vehicle_id=${encodeURIComponent(vehicleId)}&minutes=${minutes}`,
  )
}

export interface MbmtBoardRow {
  kind: 'arrival' | 'departure'
  vehicle_id: string
  driver_name: string
  schedule_code: string
  schedule_shift: string
  scheduled_ts: number
  route_code: string
  on_air: boolean
  moving: boolean
  soc_pct: number | null
  speed_kph: number | null
  legs_done: number
  running_route: string | null
  running_direction: string | null
  running_since_ts: number | null
  expected_soc_pct: number | null
  soc_rate_pct_per_hr: number | null
  /** Why expected_soc_pct is (or is not) trustworthy -- rendered, never hidden. */
  soc_basis: string
  /** Arrival rows only: has this vehicle actually closed a leg since its scheduled
   * outshed and is not currently mid-leg -- the terminal "back at the depot" state, as
   * opposed to genuinely still not returned. Absent on departure rows. */
  arrived?: boolean
  /** Arrival rows only: the closed leg's own ended_ts that confirmed `arrived` -- absent
   * when arrival was confirmed via at_depot instead (no leg to date it from). Delay must
   * relate to the schedule's own expected time and nothing else: once set, this is what
   * "how late" should be computed against, not the current clock. */
  arrived_ts?: number | null
  /** Departure rows only: has this vehicle opened a leg, or closed one, since this
   * duty's scheduled departure -- the terminal "it left" state, checked once rather than
   * re-derived from live on_air/moving at whatever instant the board happens to be
   * queried (which reads DELAYED forever the moment a genuinely-departed bus pauses
   * between legs). Absent on arrival rows. */
  departed?: boolean
  /** Departure rows only: the earliest real leg activity at or after the scheduled
   * departure -- the actual moment it left, for computing a delay that freezes once
   * departure is confirmed instead of climbing against the current clock. */
  departed_ts?: number | null
  /** Departure rows only: GPS-confirmed inside the depot boundary right now (no exit
   * geotag yet). On direct instruction: a bus still sitting at the depot past its
   * due-out time may be held for maintenance, a driver swap, etc -- that's a normal
   * depot-side hold, not a schedule failure, so it must not read DELAYED. Absent on
   * arrival rows. */
  at_depot?: boolean
  /** False when this row has no TIMS duty behind it at all -- GPS (site_stage_visit)
   * confirmed the arrival/departure independently, but TIMS is the only source of truth
   * for scheduling (the data lake is downstream of it, not independent), so there is no
   * scheduled_ts worth comparing against and no early/late status to compute. scheduled_ts
   * on these rows is the observed event time itself, for display/sort only -- statusOf
   * must check this before touching the delay math. */
  has_duty: boolean
  /** Arrival / depot-return only: Google traffic ETA minutes to depot centroid. Absent
   * on departures, arrived rows, and when the Routes key/cache miss yields nothing. */
  eta_depot_traffic_min?: number | null
  eta_depot_traffic_s?: number | null
  eta_depot_traffic_ts?: number | null
  eta_depot_source?: string | null
}

export async function fetchMbmtBoard(): Promise<{
  rows: MbmtBoardRow[]
  now_ts: number
  day: string
  duties_error: string | null
}> {
  const data = await mbmtFetch<{
    rows?: MbmtBoardRow[]
    now_ts?: number
    day?: string
    duties_error?: string | null
  }>('/api/mbmt/board')
  return {
    rows: data.rows ?? [],
    now_ts: data.now_ts ?? Date.now(),
    day: data.day ?? '',
    duties_error: data.duties_error ?? null,
  }
}

export interface MbmtRouteStop {
  /** 'up' | 'down' -- Up and Down are two different physical stop sequences (a loop's
   * outbound and return legs), not one list; seq restarts at 1 for each. */
  direction: string
  seq: number
  source: string
  stage_id: string | null
  name: string
  lat: number
  lon: number
  along: number | null
}

/** Omit direction for both legs (each stop still says which one it's on); pass
 * 'up'/'down' to get only the leg a selected bus is actually running. */
export async function fetchMbmtRouteStops(
  routeCode: string, direction = '',
): Promise<MbmtRouteStop[]> {
  const dir = direction ? `&direction=${encodeURIComponent(direction)}` : ''
  const data = await mbmtFetch<{ stops: MbmtRouteStop[] }>(
    `/api/mbmt/route-stops?route_code=${encodeURIComponent(routeCode)}${dir}`,
  )
  return data.stops ?? []
}

/** Which stop the bus is still approaching (Command Centre rework Phase 4).
 *
 *  Snap to the geometrically nearest stop (haversine, ties broken by the lowest `seq` so
 *  fetch/row order can never decide it -- a loop's up/down stops can physically coincide),
 *  then pick the end of the segment the bus is on: the stop ahead of `nearest` when the
 *  previous stop is closer than the next one (just left / still on the outbound half), or
 *  `nearest` itself when the bus is closer to that stop than to the previous one (still
 *  approaching it). Blindly returning `nearest.seq + 1` jumped one stop early for the
 *  second half of every inter-stop segment -- closer to B than to A highlighted C while
 *  the bus was still approaching B. Product decision: keep the amber bead on the stop
 *  still being approached.
 *
 *  Within 80m of `nearest` (same radius `stopArrivalTimes` uses for a pass) the bus has
 *  arrived, so the highlight advances to the next seq when one exists -- correct for a bus
 *  passing through at speed. `opts.moving === false` is the one exception: a bus that has
 *  stopped (a scheduled layover, not a drive-through) stays on the stop it arrived at
 *  instead of advancing to the next one, which otherwise reads as "Arriving" at a stop the
 *  bus hasn't moved toward for the whole idle window (live bug, confirmed against
 *  production MBMT data 2026-10-11). Last stop with nothing ahead stays highlighted either
 *  way -- blanking the bead at the terminus reads as a bug. Returns null only for an empty
 *  usable list or a non-finite position. */
export function nextRouteStop(
  stops: MbmtRouteStop[], pos: { lat: number; lon: number }, opts?: { moving?: boolean },
): MbmtRouteStop | null {
  // A stop with a non-finite coordinate must never become `nearest` -- haversineKm(..., NaN,
  // ...) is NaN, every later `d < best` comparison against a NaN `best` is false, and the
  // bad row would silently win by sheer accident of being stops[0] (found on adversarial
  // review). `stopBeadFeatures` already drops these from what actually reaches the map;
  // this function must agree, not just happen to filter them out downstream.
  const usable = stops.filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lon))
  if (usable.length === 0) return null
  if (!Number.isFinite(pos.lat) || !Number.isFinite(pos.lon)) return null
  let nearest = usable[0]!
  let best = haversineKm(pos.lat, pos.lon, nearest.lat, nearest.lon)
  for (const s of usable) {
    const d = haversineKm(pos.lat, pos.lon, s.lat, s.lon)
    if (d < best || (d === best && s.seq < nearest.seq)) { nearest = s; best = d }
  }
  // Up and Down are two separate seq sequences (seq restarts at 1 per direction) that can
  // arrive here merged -- the caller passes both legs whenever a vehicle's run direction
  // isn't resolved yet (fetchMbmtRouteStops with no `direction`). Walking ahead/behind over
  // the merged list let a same-or-lower seq number from the OTHER leg win, pointing the
  // highlight down the wrong direction entirely. Scope the walk to nearest's own leg.
  const leg = usable.filter((s) => s.direction === nearest.direction)
  let ahead: MbmtRouteStop | null = null
  let behind: MbmtRouteStop | null = null
  for (const s of leg) {
    if (s.seq > nearest.seq && (ahead === null || s.seq < ahead.seq)) ahead = s
    if (s.seq < nearest.seq && (behind === null || s.seq > behind.seq)) behind = s
  }
  if (ahead == null) return nearest
  // Arrived: same 80m pass radius as stopArrivalTimes. Advance to the next stop, UNLESS
  // the bus has actually stopped moving -- then it stays on the one it just arrived at.
  if (best * 1000 <= 80) return opts?.moving === false ? nearest : ahead
  if (behind == null) return ahead
  const dBehind = haversineKm(pos.lat, pos.lon, behind.lat, behind.lon)
  const dAhead = haversineKm(pos.lat, pos.lon, ahead.lat, ahead.lon)
  // Closer to the next neighbour than the previous → on the outbound side of nearest →
  // still approaching `ahead`. Otherwise still approaching `nearest`.
  return dAhead < dBehind ? ahead : nearest
}

/** Same 80m pass radius nextRouteStop()'s own "arrived" check above uses -- a vehicle's
 *  RAW last-reported position within this of a stop counts as having reached it. Used
 *  by the Fleet drawer's ETA panel to show "Arriving" the moment the live poll agrees,
 *  rather than waiting up to 45s for the next Google Directions refresh to say so.
 *  Deliberately checked against the RAW fix, not the map marker's tweened display
 *  position (fixTween.ts) -- the marker can sit up to one real fix gap behind, and the
 *  two disagreeing would read as a bug even though each is correct for what it tracks. */
export function hasReachedStop(
  pos: { lat: number; lon: number }, stop: { lat: number; lon: number } | null,
): boolean {
  if (!stop) return false
  if (!Number.isFinite(pos.lat) || !Number.isFinite(pos.lon)) return false
  if (!Number.isFinite(stop.lat) || !Number.isFinite(stop.lon)) return false
  return haversineKm(pos.lat, pos.lon, stop.lat, stop.lon) * 1000 <= 80
}

export type MbmtRunDirection = 'up' | 'down'

export function normalizeRunDirection(
  d: string | null | undefined,
): MbmtRunDirection | null {
  const x = (d || '').trim().toLowerCase()
  return x === 'up' || x === 'down' ? x : null
}

/** Stop geotags for a vehicle/day, oldest→newest, optionally scoped to one route. */
function stopGeotagsChronological(
  geotags: MbmtGeotagEvent[],
  routeCode?: string,
): MbmtGeotagEvent[] {
  const route = (routeCode || '').trim()
  return geotags
    .filter((g) => g.kind === 'stop' && typeof g.seq === 'number' && Number.isFinite(g.seq))
    .filter((g) => !route || !g.route_code || g.route_code === route)
    .slice()
    .sort((a, b) => a.ts - b.ts)
}

/**
 * Which Up/Down leg the bus is on, from recent stop geotags — not nearest-stop geometry.
 *
 * Sticky GPS nearest-stop flips on shared Up/Down termini (same physical bay, two rows
 * ~35m apart). Geotag history is the shared truth:
 *  - two+ tags with the same direction → that direction (seq rising confirms forward travel)
 *  - two+ tags that disagree → latest tag's direction (turnaround)
 *  - one tag at source (seq 1) or destination (max seq on that leg) → that direction
 *  - one mid-route tag with a recorded direction → still trust it (best available signal)
 */
export function inferDirectionFromGeotags(
  geotags: MbmtGeotagEvent[],
  opts?: { routeCode?: string; stops?: MbmtRouteStop[] },
): MbmtRunDirection | null {
  const tags = stopGeotagsChronological(geotags, opts?.routeCode)
  if (tags.length === 0) return null

  const maxSeqFor = (dir: MbmtRunDirection): number | null => {
    if (!opts?.stops?.length) return null
    let m = 0
    for (const s of opts.stops) {
      if (normalizeRunDirection(s.direction) === dir && s.seq > m) m = s.seq
    }
    return m > 0 ? m : null
  }

  if (tags.length === 1) {
    const g = tags[0]!
    const dir = normalizeRunDirection(g.direction)
    if (!dir) return null
    const maxSeq = maxSeqFor(dir)
    // Source / destination are definitive; mid-route still uses the geotag's direction.
    if (g.seq === 1 || (maxSeq != null && g.seq === maxSeq) || maxSeq == null) return dir
    return dir
  }

  const a = tags[tags.length - 2]!
  const b = tags[tags.length - 1]!
  const da = normalizeRunDirection(a.direction)
  const db = normalizeRunDirection(b.direction)
  if (da && db && da === db) return da
  if (db) return db
  if (da) return da
  return null
}

/**
 * Next stop on the geotag-inferred leg. Falls back to plain `nextRouteStop` when geotags
 * cannot resolve a direction. After the last stop geotag at seq S, never points at a stop
 * with seq ≤ S unless the bus is still within 80m of that stop (still dwelling).
 *
 * `opts.moving === false` stays on the stop the bus has actually arrived at/near instead
 * of advancing to the one after it -- see `nextRouteStop`'s own doc for why. Omitted (or
 * `true`) is byte-for-byte the previous behavior.
 */
export function nextRouteStopFromGeotags(
  stops: MbmtRouteStop[],
  geotags: MbmtGeotagEvent[],
  pos: { lat: number; lon: number },
  opts?: { routeCode?: string; moving?: boolean },
): MbmtRouteStop | null {
  const dir = inferDirectionFromGeotags(geotags, { routeCode: opts?.routeCode, stops })
  const leg = dir
    ? stops.filter((s) => normalizeRunDirection(s.direction) === dir)
    : stops
  if (leg.length === 0) return null

  const tags = stopGeotagsChronological(geotags, opts?.routeCode)
    .filter((g) => !dir || !g.direction || normalizeRunDirection(g.direction) === dir)
  const last = tags[tags.length - 1]
  const gpsNext = nextRouteStop(leg, pos, { moving: opts?.moving })

  if (last && typeof last.seq === 'number' && dir) {
    // Narrowing last.seq to `number` above does not survive into the .filter()
    // closures below -- TS re-widens a narrowed property access (as opposed to a
    // narrowed local variable) across a function boundary. Extracting it to a
    // const here is the fix, not a behavior change.
    const lastSeq = last.seq
    const atLast = leg.find((s) => s.seq === lastSeq)
    if (
      atLast
      && Number.isFinite(atLast.lat)
      && Number.isFinite(atLast.lon)
      && Number.isFinite(pos.lat)
      && Number.isFinite(pos.lon)
      && haversineKm(pos.lat, pos.lon, atLast.lat, atLast.lon) * 1000 <= 80
    ) {
      if (opts?.moving === false) return atLast
      const ahead = leg
        .filter((s) => s.seq > lastSeq)
        .sort((a, b) => a.seq - b.seq)[0]
      return ahead ?? atLast
    }
    // `last` is a site_route_stop_visit row: its seq/name were snapshotted from
    // site_route_stop at write time, with no FK back to it. A manual Excel resurvey
    // DELETEs and re-INSERTs a route's whole stop list (confirmed in git history, e.g.
    // a540ccf splitting Up/Down into separate per-direction sequences) -- every visit row
    // written before that keeps a seq the CURRENT leg may no longer carry at all. Treating
    // such a seq as a real anchor made "never regress below lastSeq" impossible to satisfy
    // for any real stop once lastSeq exceeded the leg's own max (confirmed live: route 24
    // 'down', a seq-50 visit row against a 20-stop current leg), falling through to the
    // leg's terminus regardless of where the bus actually was. `atLast` undefined here
    // means no stop on the CURRENT leg carries this seq at all -- the anchor cannot be
    // trusted, so fall back to pure geometry, the same fallback already used when geotags
    // can't resolve a direction at all.
    if (!atLast) return gpsNext
    // Past the last geotagged stop: never regress to seq ≤ last.seq on this leg.
    if (gpsNext && gpsNext.seq > lastSeq) return gpsNext
    const ahead = leg
      .filter((s) => s.seq > lastSeq)
      .sort((a, b) => a.seq - b.seq)[0]
    if (ahead) return ahead
    // Terminus of this leg.
    return [...leg].sort((a, b) => a.seq - b.seq).pop() ?? gpsNext
  }

  return gpsNext
}

/** For each stop, the earliest GPS trail point within `thresholdM` of it -- when the trail
 *  passes a stop more than once in a day (multiple legs), only the first pass is kept, not
 *  the last or a list of every pass. Keyed by `${direction}:${seq}`, not `stage_id` (can be
 *  null) or `name` (not guaranteed unique) -- a loop's up/down stops restart `seq` at 1 and
 *  must not collapse into one entry. A stop the trail never came near is simply absent from
 *  the returned Map, not present with an undefined/null value. */
export function stopArrivalTimes(
  stops: MbmtRouteStop[], points: MbmtTrackPoint[], thresholdM = 80,
): Map<string, number> {
  const out = new Map<string, number>()
  for (const s of stops) {
    let earliest: number | null = null
    for (const p of points) {
      if (haversineKm(p.lat, p.lon, s.lat, s.lon) * 1000 <= thresholdM) {
        if (earliest == null || p.ts < earliest) earliest = p.ts
      }
    }
    if (earliest != null) out.set(`${s.direction}:${s.seq}`, earliest)
  }
  return out
}

export interface MbmtGeotagEvent {
  kind: 'stop' | 'boundary' | string
  ts: number
  name: string | null
  route_code?: string
  /** 'stop' events only. Absent on rows logged before this column existed. */
  direction?: string | null
  seq?: number
  lat?: number
  lon?: number
  stage_id?: string
  exited_ts?: number | null
}

export async function fetchMbmtGeotags(
  vehicleId: string, date = '',
): Promise<MbmtGeotagEvent[]> {
  const q = date ? `&date=${encodeURIComponent(date)}` : ''
  const data = await mbmtFetch<{ events: MbmtGeotagEvent[] }>(
    `/api/mbmt/geotags?vehicle_id=${encodeURIComponent(vehicleId)}${q}`,
  )
  return data.events ?? []
}

export type TripSequenceBreakReason =
  | 'Direction changed'
  | 'Short trip'
  | 'Sequence broken'
  | null

export interface TripSequenceStop {
  seq: number
  name: string
  ts: number | null
  onSequence: boolean
}

/** Phase B: stamped stop sequence until a break, then further geotags (no fake seq). */
export interface TripStopSequence {
  stamped: TripSequenceStop[]
  breakReason: TripSequenceBreakReason
  further: MbmtGeotagEvent[]
}

/**
 * Walk geotags inside a trip window against the planned route-stop list. Stamps in
 * ascending seq until direction flips, a short-trip flag fires, or seq regresses /
 * skips backward — then emits a banner reason and lists remaining geotags as further.
 */
export function buildTripStopSequence(opts: {
  stops: MbmtRouteStop[]
  geotags: MbmtGeotagEvent[]
  startedTs: number | null
  endedTs: number | null
  isShort?: boolean
  expectedDirection?: string | null
}): TripStopSequence {
  const start = opts.startedTs ?? Number.NEGATIVE_INFINITY
  const end = opts.endedTs ?? Number.POSITIVE_INFINITY
  const inWindow = [...opts.geotags]
    .filter((g) => Number.isFinite(g.ts) && g.ts >= start && g.ts <= end)
    .sort((a, b) => a.ts - b.ts)

  if (opts.isShort) {
    return { stamped: [], breakReason: 'Short trip', further: inWindow }
  }

  const planned = [...opts.stops]
    .filter((s) => {
      if (!opts.expectedDirection) return true
      return String(s.direction || '').toLowerCase() === String(opts.expectedDirection).toLowerCase()
    })
    .sort((a, b) => a.seq - b.seq)

  if (planned.length === 0) {
    return { stamped: [], breakReason: null, further: inWindow }
  }

  const stamped: TripSequenceStop[] = []
  const orphans: MbmtGeotagEvent[] = []
  let expectIdx = 0
  let breakReason: TripSequenceBreakReason = null
  let furtherFrom = inWindow.length

  for (let i = 0; i < inWindow.length; i++) {
    const g = inWindow[i]!
    if (g.kind === 'stop' && opts.expectedDirection && g.direction) {
      if (String(g.direction).toLowerCase() !== String(opts.expectedDirection).toLowerCase()) {
        breakReason = 'Direction changed'
        furtherFrom = i
        break
      }
    }
    // Prefer matching the next planned stop by seq, then by name.
    const next = planned[expectIdx]
    if (!next) {
      furtherFrom = i
      break
    }
    const sameSeq = g.seq != null && g.seq === next.seq
    const sameName = !!g.name && !!next.name
      && g.name.trim().toLowerCase() === next.name.trim().toLowerCase()
    if (g.kind === 'stop' && g.seq != null && g.seq < next.seq && !sameName) {
      breakReason = 'Sequence broken'
      furtherFrom = i
      break
    }
    if (sameSeq || sameName) {
      stamped.push({ seq: next.seq, name: next.name, ts: g.ts, onSequence: true })
      expectIdx += 1
      continue
    }
    // Any later stop seq while still expecting `next` means we skipped planned stops.
    if (g.kind === 'stop' && g.seq != null && g.seq > next.seq && !sameName) {
      breakReason = 'Sequence broken'
      furtherFrom = i
      break
    }
    // Boundary / unmatched geotag before the next stop — keep looking for the next
    // planned stop, but retain the event so it still appears under "Further geotags"
    // (AC-B2). Never silently drop.
    orphans.push(g)
  }

  return {
    stamped,
    breakReason,
    further: breakReason != null || furtherFrom < inWindow.length
      ? [...orphans, ...inWindow.slice(furtherFrom)]
      : orphans,
  }
}

/** A whole duty's worth of GPS + the stops and alerts logged along the way -- unlike
 * MbmtReplayClip (breakdown-incident-scoped, a 10min lead-in), this spans the vehicle's
 * actual day so far: assembled client-side from fetchMbmtTrack + fetchMbmtGeotags +
 * whichever of today's actionables belong to this plate, not one dedicated endpoint. */
export interface MbmtDutyClip {
  vehicle_id: string
  started_ts: number
  ended_ts: number
  points: MbmtTrackPoint[]
  stops: MbmtGeotagEvent[]
  alerts: MbmtActionable[]
  /** From the live vehicle snapshot at the moment playback opened -- null when the plate
   *  wasn't found there (vehicle off-air), never fabricated from a neighbour's route. */
  route_code: string | null
  /** Freshly re-read from TIMS (fetchMbmtDuties()) when playback opens, not the ambient
   *  ~60s live-feed cache -- null when that plate has no TIMS duty today, never another
   *  plate's row. */
  driver_name: string | null
  schedule_code: string | null
  schedule_shift: string | null
}

/** One TIMS duty row, raw passthrough from GET /api/mbmt/duties -- TIMS's own field
 *  casing (vehiclenumber/drivername/schedulecode/scheduleshift), not a shape this
 *  console remaps. Loosely typed on purpose: many more fields exist on the real record
 *  that nothing here reads. */
export type MbmtTimsDutyRow = Record<string, unknown>

export async function fetchMbmtDuties(): Promise<MbmtTimsDutyRow[]> {
  const data = await mbmtFetch<{ duties?: MbmtTimsDutyRow[] }>('/api/mbmt/duties')
  return data.duties ?? []
}

/** Same plate normalization as serving/mbmt.py's `_plate()` -- uppercase, then strip
 *  everything that isn't A-Z or 0-9 -- so this can never disagree with `_duty_for()`
 *  about which row matches a plate. */
function plate(raw: unknown): string {
  return String(raw ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '')
}

export function mbmtDutyFor(
  vehicleId: string, rows: MbmtTimsDutyRow[],
): MbmtTimsDutyRow | undefined {
  const needle = plate(vehicleId)
  if (!needle) return undefined
  return rows.find((row) => plate(row.vehiclenumber) === needle)
}

/** Shortest-angular-path interpolation between two compass headings (0-360, clockwise) --
 *  same fix as console/src/lib/tripPath.ts's positionAtTime(), just never ported here.
 *  b.heading_deg ?? a.heading_deg alone made heading a STEP function: constant for the
 *  whole segment, then snapping instantly to the next real fix's heading the moment
 *  playback crossed into it. GPS heading is notoriously noisy at low speed (a stop, a
 *  depot queue, traffic) -- two consecutive real fixes can disagree by 90-180 degrees
 *  purely from that noise, so the bus glyph visibly snapped/spun in place exactly when
 *  playback reached each new real fix, on top of position gliding smoothly the whole
 *  time. Confirmed live: reported as the bus "jumping back and forth" during playback,
 *  synced to reaching each new GPS point. Interpolating the shorter of the two possible
 *  turns (never more than 180 degrees) makes the glyph rotate smoothly across the
 *  boundary instead of snapping.*/
function interpolateHeading(a: number | null, b: number | null, t: number): number | null {
  if (a == null || b == null) return b ?? a
  let diff = b - a
  if (diff > 180) diff -= 360
  if (diff < -180) diff += 360
  return (a + diff * t + 360) % 360
}

export function positionAt(points: MbmtReplayPoint[], at: number): MbmtReplayPoint | null {
  const usable = points.flatMap((p) => {
    if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon) || p.lat == null || p.lon == null) {
      return []
    }
    return [{ ...p, lat: p.lat, lon: p.lon }]
  })
  const first = usable[0]
  if (!first) return null
  if (at <= first.ts) return first
  for (let i = 1; i < usable.length; i++) {
    const a = usable[i - 1]
    const b = usable[i]
    if (!a || !b) continue
    if (b.ts >= at) {
      const span = Math.max(1, b.ts - a.ts)
      const t = Math.max(0, Math.min(1, (at - a.ts) / span))
      return {
        ts: at,
        lat: a.lat + (b.lat - a.lat) * t,
        lon: a.lon + (b.lon - a.lon) * t,
        speed_kph: b.speed_kph ?? a.speed_kph,
        heading_deg: interpolateHeading(a.heading_deg ?? null, b.heading_deg ?? null, t),
        soc_pct: b.soc_pct ?? a.soc_pct,
      }
    }
  }
  return usable[usable.length - 1] ?? first
}

